import InventoryType from "../../shared/enums/InventoryType";
import { useGetApiData, useGetApiDataAsMutate, usePostApiData } from "shared/datalayer";
import ApiInventoryTaxConfigurationBasic from "../../shared/types/ApiInventoryTaxConfigurationBasic";
import ConsoleApi from "./enums/consoleApi";
import ApiEventAgendaWrapper from "shared/types/ApiEventAgendaWrapper";
import PublicApi from "shared/enums/PublicApi";
import ApiRateCard from "shared/types/ApiRateCard";
import ApiRegistrationEnabledFeatures from "shared/types/ApiRegistrationEnabledFeatures";
import ApiEventOrganizationBasic from "../../shared/types/ApiEventOrganizationBasic";
import ApiInventoryResponse from "shared/types/ApiInventoryResponse";
import isEmpty from "lodash/isEmpty";
import { ApiInventoryRequestInventoryType } from "shared/types/ApiInventoryRequestInventoryType";
import { useCopies } from "shared/hooks/useCopies";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import { ApiAdminAddInventoryRequest } from "shared/types/ApiAdminAddInventoryRequest";
import I18n from "shared/types/I18n";
import { successToast } from "shared/utils/toastNotification";

interface GetTaxConfigurationArgsType {
  registrationId: string;
  contextId: string;
  inventoryType: InventoryType;
}

export function useGetTaxConfiguration(props: GetTaxConfigurationArgsType) {
  return useGetApiData<ApiInventoryTaxConfigurationBasic>(
    ConsoleApi.GET_TAX_CONFIGURATION_BY_ENTITY_AND_INVENTORY_TYPE,
    {
      params: {
        registrationId: props.registrationId,
        contextEntityId: props.contextId,
        inventoryType: props.inventoryType
      }
    },
    {
      enabled: Boolean(props.registrationId) && Boolean(props.contextId) && Boolean(props.inventoryType)
    }
  );
}

export function useFetchTaxConfiguration() {
  return useGetApiDataAsMutate<ApiInventoryTaxConfigurationBasic>(
    ConsoleApi.GET_TAX_CONFIGURATION_BY_ENTITY_AND_INVENTORY_TYPE
  );
}

export function useGetEventAgenda(eventId?: string, divisionId?: string) {
  return useGetApiData<ApiEventAgendaWrapper>(
    PublicApi.EVENT_AGENDA,
    { params: { eventId, divisionId } },
    { enabled: Boolean(eventId) }
  );
}

export function useGetSponsorOrganizationList(eventId?: string) {
  return useGetApiData<Array<ApiEventOrganizationBasic>>(
    PublicApi.SPONSOR_ORGANIZATIONS,
    { params: { eventId, fetchSponsor: true } },
    { enabled: Boolean(eventId) }
  );
}

export function useGetRateCardListByRegistrationAndInventoryTypes(
  registrationId: string,
  inventoryTypes: Array<InventoryType>
) {
  return useGetApiData<Array<ApiRateCard>>(
    ConsoleApi.GET_RATE_CARD_LIST_BY_INVENTORY_TYPE,
    {
      params: { inventoryTypes: inventoryTypes.join(","), registrationId }
    },
    {
      enabled: Boolean(inventoryTypes) && Boolean(registrationId)
    }
  );
}

export function useGetEnabledFeaturesByRegistrationId({ registrationId }: { registrationId?: string }) {
  return useGetApiData<ApiRegistrationEnabledFeatures>(
    ConsoleApi.REGISTRATION_GET_ENABLED_FEATURE,
    {
      params: { id: registrationId }
    },
    {
      enabled: Boolean(registrationId)
    }
  );
}

export function useFindApplicableInventory(params: ApiInventoryRequestInventoryType) {
  return useGetApiData<Array<ApiInventoryResponse>>(
    ConsoleApi.FIND_APPLICABLE_INVENTORY,
    { params: { ...params } },
    { enabled: !isEmpty(params) && !!params.contextId }
  );
}

export function useAssignInventory({ successMessage }: { successMessage: keyof I18n }, successCallback?: () => void) {
  const copies = useCopies();
  return usePostApiData<ApiPurchaseOrder, ApiAdminAddInventoryRequest>(ConsoleApi.INVENTORY_PURCHASE_BY_ADMIN, {
    onSuccess: data => {
      successToast(copies.get(successMessage));
      if (successCallback) {
        successCallback();
      }
    }
  });
}
